/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import Model, { helper } from '@tripian/model';
import moment from 'moment';
import ReactDOM from 'react-dom';
import Button from '../../base/Button/Button';
import PreLoading from '../../base/PreLoading/PreLoading';
// import NumberInput from '../../base/NumberInput/NumberInput';
import QuestionTemplate from '../../QuestionTemplate/QuestionTemplate';
import classes from './FormTemplateProfile.scss';
import UserPasswordChange from './UserPasswordChange';
import Notify from '../../base/Notification/Notification';
import Modal from '../../base/Modal/Modal';
import DatePicker from '../../DatePicker/DatePicker';
import Input from '../../base/Input/Input';
import BackButton from '../../base/Button/Icons/BackButton/BackButton';
import Header from '../../base/Header/Header';
import BUTTON_TYPES from '../../base/Button/ButtonTypes';

interface IFormTemplateProfile {
  user: Model.User;
  profileQuestions: Model.Question[];
  updateUser: (userUpdateRequest: Model.UserUpdateRequest) => Promise<Model.User>;
  updateCallback: (updatedUser: Model.User) => void;
  cancel: () => void;
  forgotPassword: () => void;
  canChangePassword: boolean;
  t: (value: Model.TranslationKey) => string;
}

const FormTemplateProfile: React.FC<IFormTemplateProfile> = ({ user, profileQuestions, updateUser, updateCallback, cancel, forgotPassword, canChangePassword, t }) => {
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<{ show: boolean; message?: string }>({ show: false });
  const [showModal, setShowModal] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const [userUpdateRequest, setUserUpdateRequest] = useState<Model.UserUpdateRequest>({
    firstName: user.firstName,
    lastName: user.lastName,
    dateOfBirth: user.dateOfBirth ? user.dateOfBirth : undefined,
    answers: [...user.answers].sort((a, b) => a - b),
    password: undefined,
  });

  const [initialUser, setInitialUser] = useState<Model.UserUpdateRequest>({
    firstName: user.firstName,
    lastName: user.lastName,
    dateOfBirth: user.dateOfBirth ? user.dateOfBirth : undefined,
    answers: [...user.answers].sort((a, b) => a - b),
    password: undefined,
  });

  const firstNameInputRef = useRef<HTMLInputElement>(null);

  moment.locale(window.twindow.langCode);

  const areDiffObject = (prevObject: Model.UserUpdateRequest, currentObject: Model.UserUpdateRequest) => {
    const prevObjectString = JSON.stringify(prevObject);
    const currentObjectString = JSON.stringify(currentObject);

    if (prevObjectString !== currentObjectString) {
      return true;
    }

    return false;
  };

  const isLightRegistered = useMemo(() => {
    const domain = user.email.slice(user.email.indexOf('@') + 1).toLowerCase();
    if (domain === 'light-tripian.com' || domain === 'tripianguest.com') {
      return true;
    }
    return false;
  }, [user.email]);

  useEffect(() => {
    const areObjectsDifferent = areDiffObject(initialUser, userUpdateRequest);
    setIsFormChanged(areObjectsDifferent);
  }, [userUpdateRequest, initialUser]);

  useEffect(() => {
    firstNameInputRef.current?.focus();
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUserUpdateRequest = helper.deepCopy(userUpdateRequest);

    switch (event.target.name) {
      case 'firstName':
        newUserUpdateRequest.firstName = event.target.value.trim();
        break;

      case 'lastName':
        newUserUpdateRequest.lastName = event.target.value.trim();
        break;

      default:
        break;
    }

    setUserUpdateRequest(newUserUpdateRequest);
  };

  const answersChange = (answers: Array<number>) => {
    const newUserUpdateRequest = helper.deepCopy(userUpdateRequest);
    newUserUpdateRequest.answers = answers.sort((a, b) => a - b);
    setUserUpdateRequest(newUserUpdateRequest);
  };

  const dateOfBirthChange = (date: moment.Moment | null) => {
    const newUserUpdateRequest = helper.deepCopy(userUpdateRequest);

    if (date === null) {
      newUserUpdateRequest.dateOfBirth = undefined;
    } else {
      newUserUpdateRequest.dateOfBirth = date.format('YYYY-MM-DD');
    }

    setUserUpdateRequest(newUserUpdateRequest);
  };

  const handleSubmit = (event: React.FormEvent<HTMLButtonElement>) => {
    setLoading(true);
    event.preventDefault();
    event.stopPropagation();
    setError('');

    if (isFormChanged) {
      updateUser(userUpdateRequest)
        .then((userSuccessfull) => {
          setInitialUser(helper.deepCopy(userUpdateRequest));
          setSuccessMessage({ show: true, message: t('user.profile.success') });
          updateCallback(userSuccessfull);
          setLoading(false);
        })
        .catch((err) => {
          setError(err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading ? (
        <div className={classes.updateLoading}>
          <PreLoading bgColor="rgba(238, 238, 238, 0.8)" />
        </div>
      ) : null}
      <form className={classes.userUpdateForm}>
        <div className="row mb0">
          <div className="col col12 pl0">
            <BackButton text={t('trips.cloneTrip.back')} clicked={cancel} />
          </div>
        </div>
        <div className="row">
          <div className="col col12">
            <Header text={t('user.profile')} />
          </div>
        </div>
        {!isLightRegistered ? (
          <div className="row mb0">
            <div className="col col12">
              <h4 className={classes.header}>{t('user.profile.email')}</h4>
            </div>
            <div className="col col12">
              <Input type="email" disabled placeholder={user.email} name="email" value="" onChange={handleChange} autocomplete="username" />
            </div>
          </div>
        ) : null}

        {canChangePassword && (
          <div className="row">
            {showModal &&
              ReactDOM.createPortal(
                <Modal className={classes.passwordChangeModal} show={showModal} backdropClick={() => setShowModal(false)} zIndex={499}>
                  <UserPasswordChange
                    updateCallback={() => {
                      setSuccessMessage({ show: true, message: t('user.profile.changePassword.success') });
                      updateCallback(user);
                      setShowModal(false);
                    }}
                    updateUser={updateUser}
                    close={() => {
                      setShowModal(false);
                    }}
                    forgotPassword={forgotPassword}
                    t={t}
                  />
                </Modal>,
                document.getElementById('modal-root') || document.body,
              )}

            {/* <div className="col col12">
              <Button
                className="w-full"
                color="primary"
                text={t('user.profile.changePassword.button')}
                onClick={() => {
                  setShowModal(true);
                }}
              />
            </div> */}

            <div className="row mb0">
              <div className={`col col12 my2 ${classes.passwordContent}`}>
                <h4 className={classes.header}>{t('auth.login.password.label')}</h4>
                <Button
                  className={classes.forgotPasswordButton}
                  text={t('user.profile.changePassword.button')}
                  color="primary"
                  type={BUTTON_TYPES.TEXT}
                  onClick={() => {
                    setShowModal(true);
                  }}
                />
              </div>
              <div className="col col12">
                <Input type="password" disabled placeholder="********" name="password" value="" onChange={handleChange} className={classes.passwordInput} />
              </div>
            </div>
          </div>
        )}

        <div className="row mb0">
          <div className="col col12 col6-m my2 p0">
            <div className="col col12">
              <h4 className={classes.header}>{t('auth.register.name.label')}</h4>
            </div>
            <div className="col col12">
              <Input ref={firstNameInputRef} type="text" placeholder={t('auth.register.name.placeholder')} name="firstName" value={userUpdateRequest.firstName || ''} onChange={handleChange} autocomplete="first-name" />
            </div>
          </div>
          <div className="col col12 col6-m my2 p0">
            <div className="col col12">
              <h4 className={classes.header}>{t('auth.register.surname.label')}</h4>
            </div>
            <div className="col col12">
              <Input type="text" placeholder={t('auth.register.surname.placeholder')} name="lastName" value={userUpdateRequest.lastName || ''} onChange={handleChange} autocomplete="last-name" />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col col12">
            <h4 className={classes.header}>{t('user.profile.dateOfBirth')}</h4>
          </div>
          <div className="col col12">
            {/* <NumberInput defaultValue={userUpdateRequest.profile?.age || undefined} placeholder="Age" minValue={1} maxValue={115} onchange={ageChange} /> */}
            <DatePicker
              currentDate={userUpdateRequest.dateOfBirth ? moment(userUpdateRequest.dateOfBirth) : null}
              onchanged={(date: moment.Moment | null) => {
                if (date) dateOfBirthChange(date);
              }}
              isDayBlocked={(date: moment.Moment | null) => {
                if (!date) return false;
                const today = moment();
                const twelveYearsAgo = today.clone().subtract(7, 'years');
                return date.isAfter(today) || date.isAfter(twelveYearsAgo);
              }}
              openDirection="up"
              showMonthSelection
            />
            {/* <TextField type="text" name="dateOfBirth" value={userUpdateRequest.dateOfBirth || ''} placeholder="Date Of Birth" onChange={handleChange} autocomplete="date-of-birth" /> */}
          </div>
        </div>
        <div className="row">
          {profileQuestions.map((question) => (
            <div key={question.id} className="col col12">
              <QuestionTemplate question={question} answers={userUpdateRequest.answers || []} callbackAnswers={answersChange} />
            </div>
          ))}
        </div>
        <div className="row">
          <div className="col col12 center">
            <Button
              className="w-full"
              color="primary"
              text={t('user.profile.submit').toUpperCase()}
              onClick={(event) => {
                handleSubmit(event);
              }}
              disabled={userUpdateRequest.firstName === '' || userUpdateRequest.lastName === '' || !isFormChanged}
            />
          </div>
          {successMessage.show ? (
            <Notify
              type="success"
              title={t('user.profile.updateUserProfile')}
              positionX="right"
              positionY="top"
              message={successMessage.message || ''}
              onClose={() => {
                setSuccessMessage({ show: false });
              }}
              closeMs={3500}
            />
          ) : null}
          {error ? (
            <div className="col col12 my10">
              <h5>{error}</h5>
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default FormTemplateProfile;
